import React from "react";
import { MapPin, Plus, Route, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCoord, routeDistance } from "./navigationRebuildUtils";
import SavedRoutePlansPanel from "./SavedRoutePlansPanel";

export default function RoutePlannerWidget({ cursor, position, routePoints, onAddWaypoint, onClearRoute, onEnableRoute, onLoadRoute }) {
  const [open, setOpen] = React.useState(false);
  const distance = routeDistance(routePoints);

  const addPoint = (point) => {
    if (!point) return;
    onEnableRoute();
    onAddWaypoint(point);
  };

  return (
    <div className="pointer-events-none absolute right-3 top-[7.75rem] z-[950] md:right-5 md:top-40">
      <Button
        type="button"
        size="icon"
        aria-label="Open route planner"
        onClick={() => setOpen((value) => !value)}
        className="pointer-events-auto h-11 w-11 border border-white/70 bg-white/95 text-primary shadow-xl shadow-black/20 hover:bg-white dark:border-cyan-100/20 dark:bg-slate-950/95 dark:text-white dark:hover:bg-white/10 md:h-12 md:w-12"
      >
        {open ? <X className="h-5 w-5" /> : <Route className="h-5 w-5" />}
      </Button>

      {open && (
        <div className="pointer-events-auto fixed inset-x-3 bottom-[10.25rem] max-h-[42dvh] overflow-y-auto rounded-3xl border border-white/70 bg-white/94 p-3 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/94 md:static md:mt-2 md:max-h-[calc(100dvh-14rem)] md:w-72">
          <div className="mb-3">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Route planner</p>
            <h3 className="text-base font-black text-slate-950 dark:text-white">Plan waypoints</h3>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{routePoints.length} WP · {distance.toFixed(2)} NM</p>
          </div>

          <div className="grid gap-2">
            <Button type="button" variant="outline" onClick={() => addPoint(cursor)} className="justify-start bg-white/80 dark:bg-white/5">
              <Plus className="h-4 w-4" /> Add cursor point
            </Button>
            <Button type="button" variant="outline" onClick={() => addPoint(position)} className="justify-start bg-white/80 dark:bg-white/5">
              <MapPin className="h-4 w-4" /> Add vessel position
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            {routePoints.length === 0 ? (
              <p className="rounded-2xl bg-slate-100 p-3 text-xs font-semibold text-slate-600 dark:bg-white/5 dark:text-slate-300">Click the map or add a cursor point to begin.</p>
            ) : (
              routePoints.map((point, index) => (
                <div key={`${point[0]}-${point[1]}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50/90 p-2 text-xs font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                  WP {index + 1}: {formatCoord(point[0], "lat")} · {formatCoord(point[1], "lng")}
                </div>
              ))
            )}
          </div>

          <SavedRoutePlansPanel routePoints={routePoints} onLoadRoute={onLoadRoute} />
        </div>
      )}
    </div>
  );
}